import React, { Component } from 'react';
import {
  Breadcrumbs,
  IconButton,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Skeleton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { send_request } from '../../../utils/Request';
import { withParams } from '../../../Routes';
import './WorkflowSchedules.css';

class WorkflowSchedules extends Component {
  constructor(props) {
    super(props);
    this.state = {
      schedules: [],
      loading: true,
      workflowName: '',
      deleting: {},
      snackbar: { open: false, message: '', severity: 'success' },
      currentTime: new Date(),
    };
    this.countdownInterval = null;
  }

  componentDidMount() {
    this.fetchWorkflowName();
    this.fetchSchedules();
    // Update countdown every second
    this.countdownInterval = setInterval(() => {
      this.setState({ currentTime: new Date() });
    }, 1000);
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  fetchWorkflowName = async () => {
    const { id } = this.props.params;
    try {
      const response = await send_request(
        `project-service/project/${id}/metadata`, '', {}, 'GET'
      );
      const name = response?.data?.name || 'Untitled';
      this.setState({ workflowName: name });
    } catch (error) {
      console.error('Error fetching workflow name:', error);
    }
  };

  fetchSchedules = async () => {
    const { id } = this.props.params;
    this.setState({ loading: true });
    try {
      const response = await send_request(`project-service/schedules/project/${id}`, '', {}, 'get');
      const schedules = response?.data || [];
      // Group by componentId, then sort within each group by next run time
      schedules.sort((a, b) => {
        // First, group by componentId
        if (a.componentId !== b.componentId) {
          return (a.componentId || '').localeCompare(b.componentId || '');
        }
        // Then sort by next run time within the same component
        const nextRunA = this.getNextRunTime(a);
        const nextRunB = this.getNextRunTime(b);
        if (!nextRunA && !nextRunB) return 0;
        if (!nextRunA) return 1;
        if (!nextRunB) return -1;
        return nextRunA - nextRunB;
      });
      this.setState({ schedules, loading: false });
    } catch (error) {
      console.error('Error fetching schedules:', error);
      this.setState({ schedules: [], loading: false });
      this.showSnackbar('Failed to load schedules', 'error');
    }
  };

  handleDeleteSchedule = async scheduleId => {
    this.setState(prev => ({
      deleting: { ...prev.deleting, [scheduleId]: true },
    }));

    try {
      await send_request(`project-service/schedules/${scheduleId}`, '', {}, 'delete');
      this.setState(prev => ({
        schedules: prev.schedules.filter(s => s.id !== scheduleId),
        deleting: { ...prev.deleting, [scheduleId]: false },
      }));
      this.showSnackbar('Schedule deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      this.setState(prev => ({
        deleting: { ...prev.deleting, [scheduleId]: false },
      }));
      this.showSnackbar('Failed to delete schedule', 'error');
    }
  };

  // Delete all schedules in a group (for weekly days schedules)
  handleDeleteScheduleGroup = async scheduleIds => {
    const groupKey = scheduleIds.join('-');
    this.setState(prev => ({
      deleting: { ...prev.deleting, [groupKey]: true },
    }));

    try {
      // Delete all schedules in the group
      await Promise.all(
        scheduleIds.map(id => send_request(`project-service/schedules/${id}`, '', {}, 'delete'))
      );
      this.setState(prev => ({
        schedules: prev.schedules.filter(s => !scheduleIds.includes(s.id)),
        deleting: { ...prev.deleting, [groupKey]: false },
      }));
      this.showSnackbar('Schedule deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting schedule group:', error);
      this.setState(prev => ({
        deleting: { ...prev.deleting, [groupKey]: false },
      }));
      this.showSnackbar('Failed to delete schedule', 'error');
    }
  };

  handleDeleteAllSchedules = async () => {
    const { id } = this.props.params;
    if (!window.confirm('Are you sure you want to delete all schedules for this workflow?')) {
      return;
    }

    this.setState({ loading: true });
    try {
      await send_request(`project-service/schedules/project/${id}`, '', {}, 'delete');
      this.setState({ schedules: [], loading: false });
      this.showSnackbar('All schedules deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting all schedules:', error);
      this.setState({ loading: false });
      this.showSnackbar('Failed to delete schedules', 'error');
    }
  };

  showSnackbar = (message, severity) => {
    this.setState({ snackbar: { open: true, message, severity } });
  };

  handleCloseSnackbar = () => {
    this.setState(prev => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  formatInterval = intervalMinute => {
    if (!intervalMinute) return '-';
    if (intervalMinute < 60) return `Every ${intervalMinute} min`;
    if (intervalMinute < 1440) {
      const hours = Math.round(intervalMinute / 60);
      return `Every ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    if (intervalMinute < 10080) {
      const days = Math.round(intervalMinute / 1440);
      return `Every ${days} ${days === 1 ? 'day' : 'days'}`;
    }
    const weeks = Math.round(intervalMinute / 10080);
    return `Every ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  };

  // Format days of week for grouped weekly schedules
  formatWeeklyDays = schedules => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const days = schedules
      .map(s => {
        const date = this.parseDate(s.startDateTime);
        return date ? date.getDay() : null;
      })
      .filter(d => d !== null)
      .sort((a, b) => a - b);

    // Remove duplicates
    const uniqueDays = [...new Set(days)];
    const dayLabels = uniqueDays.map(d => dayNames[d]);

    if (dayLabels.length === 0) return '-';

    // Get time from the first schedule (all should be at the same time)
    const firstDate = this.parseDate(schedules[0]?.startDateTime);
    const timeStr = firstDate
      ? ` at ${String(firstDate.getHours()).padStart(2, '0')}:${String(
          firstDate.getMinutes()
        ).padStart(2, '0')}`
      : '';

    if (dayLabels.length === 1) return `Every ${dayLabels[0]}${timeStr}`;
    return `Every ${dayLabels.join(', ')}${timeStr}`;
  };

  // Check if a group of schedules represents a "weekly on specific days" pattern
  isWeeklyDaysGroup = schedules => {
    if (schedules.length <= 1) return false;
    // All schedules should have 1 week (10080 minutes) interval
    return schedules.every(s => s.intervalMinute === 10080);
  };

  // Get the nearest next run time from a group of schedules
  getNearestNextRun = schedules => {
    const nextRuns = schedules.map(s => this.getNextRunTime(s)).filter(date => date !== null);

    if (nextRuns.length === 0) return null;
    return nextRuns.reduce((nearest, current) => (current < nearest ? current : nearest));
  };

  // Group schedules by componentId
  getGroupedSchedules = () => {
    const { schedules } = this.state;
    const grouped = {};

    schedules.forEach(schedule => {
      const key = schedule.componentId || 'unknown';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(schedule);
    });

    // Convert to array and sort each group by next run time
    return Object.entries(grouped).map(([componentId, groupSchedules]) => {
      // Sort by next run time within group
      groupSchedules.sort((a, b) => {
        const nextRunA = this.getNextRunTime(a);
        const nextRunB = this.getNextRunTime(b);
        if (!nextRunA && !nextRunB) return 0;
        if (!nextRunA) return 1;
        if (!nextRunB) return -1;
        return nextRunA - nextRunB;
      });

      return {
        componentId,
        schedules: groupSchedules,
        isWeeklyDays: this.isWeeklyDaysGroup(groupSchedules),
      };
    });
  };

  formatDateTime = dateTime => {
    const date = this.parseDate(dateTime);
    if (!date) return '-';
    try {
      // Format in user's local timezone
      return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return String(dateTime);
    }
  };

  parseDate = dateValue => {
    if (!dateValue) return null;
    try {
      // Handle various date formats
      let date;
      if (typeof dateValue === 'number') {
        // Unix timestamp
        date = new Date(dateValue);
      } else if (typeof dateValue === 'string') {
        // ISO string or other string format
        date = new Date(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'object' && dateValue.$date) {
        // MongoDB extended JSON format
        date = new Date(dateValue.$date);
      } else {
        return null;
      }
      // Check if date is valid
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  getNextRunTime = schedule => {
    const startDate = this.parseDate(schedule.startDateTime);
    if (!startDate) return null;

    try {
      const now = new Date();
      const intervalMs = (schedule.intervalMinute || 0) * 60 * 1000;

      // If start date is in the future, that's the next run
      if (startDate > now) {
        return startDate;
      }

      // If no interval, it was a one-time schedule
      if (!intervalMs) {
        return null;
      }

      // Calculate next run based on interval
      const timeSinceStart = now - startDate;
      const intervalsPassed = Math.floor(timeSinceStart / intervalMs);
      const nextRun = new Date(startDate.getTime() + (intervalsPassed + 1) * intervalMs);
      return nextRun;
    } catch {
      return null;
    }
  };

  formatTimeUntil = targetDate => {
    if (!targetDate) return 'Completed';
    const now = this.state.currentTime;
    const diffMs = targetDate - now;

    if (diffMs < 0) return 'Running soon';

    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);

    if (days > 0) {
      return `${days}d ${hours}h ${mins}m ${secs}s`;
    }
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Get heatmap class based on time remaining
  // Red = very soon, Green = plenty of time
  getHeatmapClass = targetDate => {
    if (!targetDate) return 'completed';
    const now = this.state.currentTime;
    const diffMs = targetDate - now;

    if (diffMs < 0) return 'heatmap-critical'; // Already passed, running soon
    if (diffMs < 30 * 60 * 1000) return 'heatmap-critical'; // < 30 min - red
    if (diffMs < 60 * 60 * 1000) return 'heatmap-warning'; // 30 min - 1 hour - orange
    if (diffMs < 2 * 60 * 60 * 1000) return 'heatmap-caution'; // 1-2 hours - yellow
    if (diffMs < 6 * 60 * 60 * 1000) return 'heatmap-moderate'; // 2-6 hours - light green
    return 'heatmap-safe'; // 6+ hours - green
  };

  // Get a display name for the component - use componentId if it's a readable name,
  // otherwise fall back to "Schedule Start"
  getComponentName = componentId => {
    if (!componentId) return 'Schedule Start';
    // Check if componentId looks like a UUID (36 chars with dashes)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(componentId)) {
      return 'Schedule Start';
    }
    return componentId;
  };

  render() {
    const { id } = this.props.params;
    const { isModal } = this.props;
    const { schedules, loading, workflowName, deleting, snackbar } = this.state;

    return (
      <div className="workflow-schedules-container">
        {!isModal && (
          <Breadcrumbs separator=">" className="breadcrumbs" style={{ marginBottom: 16 }}>
            <Link className="breadcrumbItem" to="/project">
              Workflows
            </Link>
            <Link className="breadcrumbItem" to={`/project/canvas/${id}`}>
              {workflowName || 'Workflow'}
            </Link>
            <span className="breadcrumbItem">Schedules</span>
          </Breadcrumbs>
        )}

        <div className="workflow-schedules-header">
          {!isModal && (
            <IconButton size="small" onClick={() => this.props.navigate(`/project/canvas/${id}`)}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <h1 className="workflow-schedules-title">
            Schedules {!isModal && `for ${workflowName || 'Workflow'}`}
          </h1>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={this.fetchSchedules}
              disabled={loading}
              className="schedules-refresh-btn"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>

        <div className="schedules-info-banner">
          <InfoOutlinedIcon fontSize="small" style={{ color: '#a3a3a3', flexShrink: 0 }} />
          <p>
            Active scheduled triggers for this workflow. Schedules are created when a workflow with
            a Schedule Start component is published.
          </p>
        </div>

        {loading ? (
          <div className="schedules-table-wrapper">
            <table className="schedules-table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Next run</th>
                  <th>Frequency</th>
                  <th>Repeats</th>
                  <th>Next run</th>
                  <th style={{ textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4].map(rowIndex => (
                  <tr key={rowIndex}>
                    <td>
                      <Skeleton variant="text" width={90} height={20} />
                    </td>
                    <td>
                      <Skeleton variant="text" width={140} height={20} />
                    </td>
                    <td>
                      <Skeleton variant="text" width={100} height={20} />
                    </td>
                    <td>
                      <Skeleton variant="text" width={50} height={20} />
                    </td>
                    <td>
                      <Skeleton variant="rounded" width={70} height={22} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Skeleton
                        variant="circular"
                        width={24}
                        height={24}
                        style={{ margin: '0 auto' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : schedules.length === 0 ? (
          <div className="empty-schedules">
            <ScheduleIcon className="empty-schedules-icon" />
            <p className="empty-schedules-text">No active schedules found for this workflow.</p>
          </div>
        ) : (
          <>
            <div className="schedules-table-wrapper">
              <table className="schedules-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Next run</th>
                    <th>Frequency</th>
                    <th>Repeats</th>
                    <th>Next run</th>
                    <th style={{ textAlign: 'center' }}>
                      <button className="delete-all-btn" onClick={this.handleDeleteAllSchedules}>
                        Delete all
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {this.getGroupedSchedules().map(group => {
                    const { componentId, schedules: groupSchedules, isWeeklyDays } = group;

                    // For weekly days pattern, render as single row
                    if (isWeeklyDays) {
                      const nearestRun = this.getNearestNextRun(groupSchedules);
                      const timeUntil = this.formatTimeUntil(nearestRun);
                      const heatmapClass = this.getHeatmapClass(nearestRun);
                      const scheduleIds = groupSchedules.map(s => s.id);
                      const groupKey = scheduleIds.join('-');
                      const firstSchedule = groupSchedules[0];

                      return (
                        <tr key={groupKey}>
                          <td className="schedule-component">
                            <a
                              href={`/project/canvas/${id}?view=${componentId}`}
                              className="schedule-component-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => {
                                if (this.props.isModal && this.props.onClose) {
                                  e.preventDefault();
                                  window.open(
                                    `/project/canvas/${id}?view=${componentId}`,
                                    '_blank',
                                    'noopener,noreferrer'
                                  );
                                }
                              }}
                            >
                              {this.getComponentName(componentId)}
                            </a>
                          </td>
                          <td className="schedule-datetime">
                            <Tooltip title={nearestRun ? this.formatDateTime(nearestRun) : '-'}>
                              <span>{nearestRun ? this.formatDateTime(nearestRun) : '-'}</span>
                            </Tooltip>
                          </td>
                          <td className="schedule-interval">
                            {this.formatWeeklyDays(groupSchedules)}
                          </td>
                          <td>
                            {firstSchedule.repeat === -1
                              ? 'Forever'
                              : firstSchedule.repeat || 'Once'}
                          </td>
                          <td>
                            <span className={`schedule-status-chip ${heatmapClass}`}>
                              {timeUntil}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Tooltip title="Delete schedule">
                              <button
                                className="schedule-delete-btn"
                                onClick={() => this.handleDeleteScheduleGroup(scheduleIds)}
                                disabled={deleting[groupKey]}
                              >
                                {deleting[groupKey] ? (
                                  <CircularProgress size={12} style={{ color: '#a3a3a3' }} />
                                ) : (
                                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                )}
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    }

                    // For non-weekly-days groups, render each schedule as a separate row
                    return groupSchedules.map(schedule => {
                      const nextRun = this.getNextRunTime(schedule);
                      const timeUntil = this.formatTimeUntil(nextRun);
                      const heatmapClass = this.getHeatmapClass(nextRun);

                      return (
                        <tr key={schedule.id}>
                          <td className="schedule-component">
                            <a
                              href={`/project/canvas/${id}?view=${schedule.componentId}`}
                              className="schedule-component-link"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => {
                                if (this.props.isModal && this.props.onClose) {
                                  e.preventDefault();
                                  window.open(
                                    `/project/canvas/${id}?view=${schedule.componentId}`,
                                    '_blank',
                                    'noopener,noreferrer'
                                  );
                                }
                              }}
                            >
                              {this.getComponentName(schedule.componentId)}
                            </a>
                          </td>
                          <td className="schedule-datetime">
                            <Tooltip title={this.formatDateTime(schedule.startDateTime)}>
                              <span>{nextRun ? this.formatDateTime(nextRun) : '-'}</span>
                            </Tooltip>
                          </td>
                          <td className="schedule-interval">
                            {this.formatInterval(schedule.intervalMinute)}
                          </td>
                          <td>{schedule.repeat === -1 ? 'Forever' : schedule.repeat || 'Once'}</td>
                          <td>
                            <span className={`schedule-status-chip ${heatmapClass}`}>
                              {timeUntil}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <Tooltip title="Delete schedule">
                              <button
                                className="schedule-delete-btn"
                                onClick={() => this.handleDeleteSchedule(schedule.id)}
                                disabled={deleting[schedule.id]}
                              >
                                {deleting[schedule.id] ? (
                                  <CircularProgress size={12} style={{ color: '#a3a3a3' }} />
                                ) : (
                                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                )}
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={this.handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

export default withParams(WorkflowSchedules);
